import '../App.css';
import {
    Container, Card, Space, Anchor, Text,
    createStyles, rem, Group, Divider, Accordion, Badge, Box
} from '@mantine/core';
import Footer from '../components/Footer';
import Head from '../components/Head';
import ReactMarkdown from 'react-markdown';
import { IconFileText, IconMail, IconList } from '@tabler/icons-react'

// ── Category colours ──────────────────────────────────────────────────────────
type Category = 'usage' | 'content' | 'legal'
const CAT_COLOR: Record<Category, string> = {
    usage: '#2b9eb3',  // teal   — how you use the service
    content: '#1a9e2e',  // green  — content & contributions
    legal: '#532d86',  // purple — legal provisions
}
const CAT_LABEL: Record<Category, string> = {
    usage: 'Usage',
    content: 'Content',
    legal: 'Legal',
}

// ── Styles ────────────────────────────────────────────────────────────────────
const useStyles = createStyles((theme) => ({

    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #e6f4ff 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(32),
        paddingBottom: rem(36),
        textAlign: 'center',
    },

    pageTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: rem(52),
        color: '#0048a0',

        [theme.fn.smallerThan('md')]: { fontSize: rem(38) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(28) },
    },

    dividerLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(12),
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const,
        color: theme.colors.gray[5],
    },

    sectionDivider: {
        margin: `${rem(28)} 0 ${rem(20)}`,
    },

    tocCard: {
        background: 'rgba(255,255,255,0.8)',
        borderLeft: `4px solid #0048a0`,
    },

    tocLink: {
        display: 'block',
        padding: `${rem(4)} 0`,
        fontSize: rem(14),
        color: '#0048a0',
        textDecoration: 'none',
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 500,
        transition: 'color 0.15s ease, padding-left 0.15s ease',

        '&:hover': {
            color: '#002e70',
            paddingLeft: rem(6),
        },
    },

    sectionCard: {
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: theme.shadows.sm },
    },

    sectionTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(20),
    },

    prose: {
        fontSize: rem(15),
        lineHeight: 1.7,
        color: theme.colors.gray[8],
    },

    contactCard: {
        background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
        border: `2px solid #2b9eb3`,
        borderRadius: theme.radius.lg,
        padding: `${rem(24)} ${rem(32)}`,
        textAlign: 'center',
    },
}))

// ── Section data ──────────────────────────────────────────────────────────────
interface Section {
    id: string
    title: string
    category: Category
    content: string
    collapsible?: boolean
}

const tosSections: Section[] = [
    {
        id: 'acceptance', title: 'Acceptance of the Terms of Use', category: 'usage',
        content: `
**Last Modified: 8/21/26**

These terms of use are entered into by and between You and Pobblebonk, LLC ("Company," "we," or "us"). The following terms and conditions, together with any documents they expressly incorporate by reference (collectively, "Terms of Use"), govern your access to and use of the Comedy Seen App and comedyseen.com, including any content, functionality, and services offered on or through the Comedy Seen App and comedyseen.com (collectively the "Service"), whether as a guest or a registered user.

Please read the Terms of Use carefully before you start to use the Service. **By using the Service, you accept and agree to be bound and abide by these Terms of Use and our Privacy Policy.** If you do not want to agree to these Terms of Use or the Privacy Policy, you must not access or use the Service.

This Service is offered and available to users who are **18 years of age or older**.
`
    },
    {
        id: 'changes-terms', title: 'Changes to the Terms of Use', category: 'usage',
        content: `
We may revise and update these Terms of Use from time to time in our sole discretion. All changes are effective immediately when we post them, and apply to all access to and use of the Service thereafter.

Your continued use of the Service following the posting of revised Terms of Use means that you accept and agree to the changes. You are expected to check this page from time to time so you are aware of any changes, as they are binding on you.
`
    },
    {
        id: 'accessing', title: 'Accessing the Service and Account Security', category: 'usage',
        content: `
We reserve the right to withdraw or amend this Service in our sole discretion without notice. We will not be liable if for any reason all or any part of the Service is unavailable at any time or for any period.

You are responsible for both:
* Making all arrangements necessary for you to have access to the Service.
* Ensuring that all persons who access the Service through your internet connection are aware of these Terms of Use and comply with them.

If you choose, or are provided with, a user name, password, or any other piece of information as part of our security procedures, you must treat such information as confidential, and you must not disclose it to any other person or entity. You agree to notify us immediately of any unauthorized access to or use of your user name or password or any other breach of security.

We have the right to disable any user name, password, or other identifier, whether chosen by you or provided by us, at any time in our sole discretion for any or no reason, including if, in our opinion, you have violated any provision of these Terms of Use.
`
    },
    {
        id: 'ip-rights', title: 'Intellectual Property Rights', category: 'legal',
        content: `
The Service and its entire contents, features, and functionality are owned by the Company, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.

These Terms of Use permit you to use the Service for your **personal, non-commercial use only**. You must not reproduce, distribute, modify, create derivative works of, publicly display, republish, download, store, or transmit any of the material on our Service, except as follows:
* Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials.
* You may store files that are automatically cached by your Web browser for display enhancement purposes.
* You may print one copy of a reasonable number of pages of the Service for your own personal, non-commercial use.

You must not access or use for any commercial purposes any part of the Service or any services or materials available through the Service.

If you wish to make any use of material on the Service other than that set out in this section, please address your request to: info@getcomedyseen.com.
`
    },
    {
        id: 'trademarks', title: 'Trademarks', category: 'legal',
        content: `
The Company name, the terms, Company trademarks, the Company logo, and all related names, logos, product and service names, designs, and slogans are trademarks of the Company or its affiliates or licensors. You must not use such marks without the prior written permission of the Company.
`
    },
    {
        id: 'prohibited', title: 'Prohibited Uses', category: 'usage', collapsible: true,
        content: `
You may use the Service only for lawful purposes and in accordance with these Terms of Use. You agree not to use the Service:
* In any way that violates any applicable federal, state, local, or international law or regulation.
* For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way.
* To send, knowingly receive, upload, download, use, or re-use any material that does not comply with the Content Standards set out in these Terms of Use.
* To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail," "chain letter," or "spam."
* To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity.
* To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service.

Additionally, you agree not to:
* Use the Service in any manner that could disable, overburden, damage, or impair the site.
* Use any robot, spider, or other automatic device, process, or means to access the Service for any purpose, including monitoring or copying any of the material on the Service.
* Introduce any viruses, Trojan horses, worms, logic bombs, or other material that is malicious or technologically harmful.
* Attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Service.
* Attack the Service via a denial-of-service attack or a distributed denial-of-service attack.
`
    },
    {
        id: 'user-contributions', title: 'User Contributions', category: 'content',
        content: `
The Service may contain interactive features that allow users to post, submit, publish, display, or transmit content or materials (collectively, "User Contributions") on or through the Service.

All User Contributions must comply with the Content Standards set out in these Terms of Use.

Any User Contribution you post to the site will be considered non-confidential and non-proprietary. By providing any User Contribution on the Service, you grant us and our affiliates and service providers the right to use, reproduce, modify, perform, display, distribute, and otherwise disclose to third parties any such material for any purpose.

* **For the avoidance of doubt, you retain ownership of your intellectual property (including your jokes and performance material). This license allows us to display your content within the App and for marketing the Service.**

You understand and acknowledge that you are responsible for any User Contributions you submit or contribute, and you, not the Company, have full responsibility for such content, including its legality, reliability, accuracy, and appropriateness.

If you elect to post payment account information on your personal page (i.e., Cash App, Venmo, etc.), you do so at your own risk.
`
    },
    {
        id: 'purchases', title: 'Purchases, Payments, and Ticketing', category: 'usage',
        content: `
* **Payment Processing via Stripe:** All ticket and service purchases are processed securely through Stripe. By purchasing a ticket, you authorize us and Stripe to charge your designated payment method for the total order amount, including ticket prices, applicable non-refundable service fees, and taxes. You agree to comply with Stripe's Terms of Service.
* **All Sales Are Final:** Unless an event is cancelled by the organizer or otherwise required by applicable law, all ticket sales are strictly final, non-refundable, and non-exchangeable.
* **Pricing and Billing Errors:** We make every effort to display accurate pricing, but errors may occasionally occur. If a ticket is listed or sold at an incorrect price due to a system error, we reserve the right to cancel or void the transaction and issue a full refund.
* **Chargebacks and Payment Disputes:** If you initiate an unjustified chargeback or payment dispute with your financial institution or card issuer for a validly purchased ticket, we reserve the right to suspend or terminate your account, invalidate any associated tickets, and seek recovery of the disputed amount along with any associated dispute fees.
* **Service and Convenience Fees:** You acknowledge that the Service may charge non-refundable ticketing, convenience, or service fees per ticket or order. These fees cover platform operations and transaction processing costs.
* **Platform Role:** Pobblebonk, LLC operates as a ticketing technology platform and intermediary connecting attendees with event organizers, hosts, and venues. Unless explicitly stated, Pobblebonk, LLC does not produce, manage, or control the events and is not responsible for event scheduling, venue conditions, or the content or quality of performances.
`
    },
    {
        id: 'venue-policies', title: 'Venue Policies and Age Restrictions', category: 'usage',
        content: `
* **Age Requirements:** Comedy events and venues frequently enforce minimum age restrictions (e.g., 18+, 21+). It is the ticket purchaser's sole responsibility to verify and comply with all age and identification requirements before purchasing tickets.
* **Venue Rules and Drink Minimums:** Certain venues may enforce dress codes, bag policies, or item/drink minimum purchases (e.g., "two-item minimum"). Ticket holders must comply with all venue policies.
* **Right of Admission and Ejection:** Event hosts and venue management reserve the absolute right to refuse admission or eject any attendee whose behavior is deemed disruptive, disorderly, intoxicated, or in violation of venue rules, without obligation to provide a refund or compensation.
* **Denial of Entry:** Failure to present valid government-issued photo ID or failure to meet venue-specific requirements does not entitle the ticket holder to a refund or exchange.
`
    },
    {
        id: 'ticket-resale', title: 'Ticket Resale, Transfers, and Anti-Fraud', category: 'usage',
        content: `
* **Prohibition on Scalping and Bots:** You agree not to use automated bots, scripts, or scraping tools to purchase tickets, nor to resell tickets at inflated prices above face value in violation of applicable laws.
* **Counterfeit and Duplication:** Each ticket barcode or QR code allows one entry only. Any attempt to duplicate, forge, alter, or transfer unauthorized copies of a ticket will result in automatic invalidation of the ticket without a refund and may be referred to law enforcement.
* **Authorized Sources:** Only tickets purchased directly through the official Comedy Seen App, website, or authorized partner box offices are recognized as valid for admission.
`
    },
    {
        id: 'organizer-terms', title: 'Event Organizer Terms', category: 'legal', collapsible: true,
        content: `
If you list, manage, or sell tickets for an event via the Service ("Event Organizer"), you agree to the following additional terms:

* **Event Accuracy and Fulfillment:** You are solely responsible for accurately describing event details (date, time, venue, age limits, pricing) and fulfilling the event as advertised.
* **Cancellations and Refunds:** If your event is cancelled, postponed, or materially altered, you agree to immediately notify attendees through the Service and are solely financially responsible for authorizing and funding attendee refunds.
* **Taxes:** Event Organizers are solely responsible for calculating, collecting, reporting, and remitting any applicable federal, state, or local sales, entertainment, or admissions taxes associated with their ticket sales.
* **Organizer Payouts and Reserves:** Payouts for ticket sales are processed according to our payout schedule, minus applicable platform service fees and payment processing costs. We reserve the right to hold back reserves or delay payouts in the event of suspected fraud, high chargeback risk, or unresolved cancellations.
`
    },
    {
        id: 'contests', title: 'Contests and Promotions', category: 'content',
        content: `
From time to time, we may offer sweepstakes, contests, or other promotions (collectively, "Promotions") that offer prizes based on the accumulation of points or other criteria. These Promotions are governed by separate Official Rules, which may contain eligibility requirements and other terms. If there is a conflict between these Terms of Use and the Official Rules of a Promotion, the Official Rules will control.
`
    },
    {
        id: 'monitoring', title: 'Monitoring and Enforcement; Termination', category: 'legal',
        content: `
We have the right to:
* Remove or refuse to post any User Contributions for any or no reason in our sole discretion.
* Take any action with respect to any User Contribution that we deem necessary or appropriate in our sole discretion, including if we believe that such User Contribution violates the Terms of Use.
* Disclose your identity or other information about you to any third party who claims that material posted by you violates their rights.
* Take appropriate legal action, including without limitation, referral to law enforcement, for any illegal or unauthorized use of the Service.
* Terminate or suspend your access to all or part of the Service for any or no reason.

However, we cannot review material before it is posted on the Service, and cannot ensure prompt removal of objectionable material after it has been posted. We assume no liability for any action or inaction regarding transmissions, communications, or content provided by any user or third party.
`
    },
    {
        id: 'content-standards', title: 'Content Standards', category: 'content', collapsible: true,
        content: `
These content standards apply to any and all User Contributions and use of Interactive Services. User Contributions must in their entirety comply with all applicable federal, state, local, and international laws and regulations. User Contributions must not:
* Contain any material that is defamatory, obscene, indecent, abusive, offensive, harassing, violent, hateful, inflammatory, or otherwise objectionable.
* Promote sexually explicit or pornographic material, violence, or discrimination based on race, sex, religion, nationality, disability, sexual orientation, or age.
* Infringe any patent, trademark, trade secret, copyright, or other intellectual property or other rights of any other person.
* Violate the legal rights (including the rights of publicity and privacy) of others or contain any material that could give rise to any civil or criminal liability under applicable laws or regulations.
* Be likely to deceive any person.
* Promote any illegal activity, or advocate, promote, or assist any unlawful act.
* Cause annoyance, inconvenience, or needless anxiety or be likely to upset, embarrass, alarm, or annoy any other person.
* Impersonate any person, or misrepresent your identity or affiliation with any person or organization.
* Involve commercial activities or sales, such as contests, sweepstakes, and other sales promotions, barter, or advertising.
* Give the impression that they emanate from or are endorsed by us or any other person or entity, if this is not the case.
`
    },
    {
        id: 'copyright', title: 'Copyright Infringement', category: 'legal',
        content: `
If you believe that any User Contributions violate your copyright, please send us a notice of copyright infringement to info@getcomedyseen.com. It is the policy of the Company to terminate the user accounts of repeat infringers.
`
    },
    {
        id: 'reliance', title: 'Reliance on Information Posted & Attendance at Events', category: 'usage',
        content: `
The information presented on or through the Service is made available solely for general information purposes. We do not warrant the accuracy, completeness, or usefulness of this information. Any reliance you place on such information is strictly at your own risk.

**Attendance at Events:** You acknowledge and agree that Pobblebonk, LLC does not own, operate, or control the venues or events listed on the Service. Your attendance at any open mic, showcase, or event listed on the Service is solely at your own risk. We do not inspect venues for safety, nor do we screen hosts or other attendees. You agree to hold Pobblebonk, LLC harmless from any injury, loss, or damage arising from your attendance at any physical event listed on the Service.
`
    },
    {
        id: 'misc-provisions', title: 'Miscellaneous', category: 'legal', collapsible: true,
        content: `
### Changes to the Service
We may update the content on this Service from time to time, but its content is not necessarily complete or up-to-date. Any of the material on the Service may be out of date at any given time, and we are under no obligation to update such material.

### Information About You and Your Visits to the Service
All information we collect on this Service is subject to our Privacy Policy. By using the Service, you consent to all actions taken by us with respect to your information in compliance with the Privacy Policy.

### Linking to the Service and Social Media Features
You may link to our homepage, provided you do so in a way that is fair and legal and does not damage our reputation or take advantage of it.

### Links from the Service
If the Service contains links to other sites and resources provided by third parties, these links are provided for your convenience only. We have no control over the contents of those sites or resources, and accept no responsibility for them or for any loss or damage that may arise from your use of them.

### Geographic Restrictions
The owner of the Service is based in the State of Florida in the United States. We provide this Service for use only by persons located in the United States. Access to the Service may not be legal by certain persons or in certain countries.
`
    },
    {
        id: 'disclaimers', title: 'Disclaimer of Warranties', category: 'legal', collapsible: true,
        content: `
You understand that we cannot and do not guarantee or warrant that files available for downloading from the internet or the Service will be free of viruses or other destructive code. You are responsible for implementing sufficient procedures and checkpoints to satisfy your particular requirements for anti-virus protection and accuracy of data input and output.

TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY A DISTRIBUTED DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER EQUIPMENT DUE TO YOUR USE OF THE SERVICE.

YOUR USE OF THE SERVICE, ITS CONTENT, AND ANY OFFERINGS OR ITEMS OBTAINED THROUGH THE SERVICE IS AT YOUR OWN RISK. THE SERVICE, ITS CONTENT, AND ANY OFFERINGS OR ITEMS OBTAINED THROUGH THE SERVICE ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
`
    },
    {
        id: 'liability', title: 'Limitation on Liability', category: 'legal',
        content: `
TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL THE COMPANY, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SERVICE, ANY WEBSITES LINKED TO IT, ANY CONTENT ON THE SERVICE OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
`
    },
    {
        id: 'legal-general', title: 'General Legal Provisions', category: 'legal', collapsible: true,
        content: `
### Indemnification
You agree to defend, indemnify, and hold harmless the Company, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms of Use or your use of the Service.

### Governing Law and Jurisdiction
All matters relating to the Service and these Terms of Use shall be governed by and construed in accordance with the internal laws of the State of Florida. Any legal suit, action, or proceeding arising out of or related to these Terms of Use or the Service shall be instituted exclusively in the federal courts of the United States or the courts of the State of Florida, located in the City of Miami and County of Miami-Dade.

### Arbitration
At Company's sole discretion, it may require You to submit any disputes arising from these Terms of Use or use of the Service to final and binding arbitration under the Rules of Arbitration of the American Arbitration Association applying Florida law.

### Limitation on Time to File Claims
ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO THESE TERMS OF USE OR THE SERVICE MUST BE COMMENCED WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES; OTHERWISE, SUCH CAUSE OF ACTION OR CLAIM IS PERMANENTLY BARRED.

### Waiver and Severability
No waiver by the Company of any term or condition set out in these Terms of Use shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition.

### Entire Agreement
The Terms of Use and our Privacy Policy constitute the sole and entire agreement between you and Pobblebonk, LLC regarding the Service and supersede all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral, regarding the Service.
`
    },
    {
        id: 'contact', title: 'Your Comments and Concerns', category: 'usage',
        content: `
All feedback, comments, requests for technical support, and other communications relating to the Service should be directed to: info@getcomedyseen.com.
`
    },
]

const tocSections = tosSections

// ── Main component ────────────────────────────────────────────────────────────
function Tos() {
    const { classes } = useStyles()

    const links = [
        { "link": "/About", "label": "About" },
        { "link": "/Support", "label": "Support" },
        { "link": "/Pp", "label": "Privacy" },
        { "link": "/Tos", "label": "Terms of Use" },
        { "link": "/Changelog", "label": "Changelog" },
    ]

    const renderProse = (content: string) => (
        <div className={classes.prose}>
            <ReactMarkdown>{content}</ReactMarkdown>
        </div>
    )

    return (
        <div className="tos-wrapper">
            <Head />

            {/* ── Hero ── */}
            <div className={classes.heroWrapper}>
                <Group position="center" spacing="xs" mb={rem(8)}>
                    <IconFileText size={44} color="#0048a0" />
                </Group>
                <Text className={classes.pageTitle}>Terms of Use</Text>
                <Text size="lg" color="dimmed" mt={6}>Please read carefully before using Comedy Seen</Text>
            </div>

            <Container size="md" py="xl">

                {/* ── Table of Contents ── */}
                <Divider
                    className={classes.sectionDivider}
                    label={<span className={classes.dividerLabel}><IconList size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Contents</span>}
                    labelPosition="center"
                    size="xs"
                />

                <Card className={classes.tocCard} shadow="xs" padding="lg" radius="md" withBorder mb="xl">
                    <Box sx={{ columns: '2 200px', columnGap: '2rem' }}>
                        {tocSections.map(s => (
                            <a key={s.id} href={`#${s.id}`} className={classes.tocLink}>
                                {s.title}
                            </a>
                        ))}
                    </Box>
                </Card>

                {/* ── Sections ── */}
                {tosSections.map((section) => {
                    const borderColor = CAT_COLOR[section.category]

                    return (
                        <Card
                            key={section.id}
                            id={section.id}
                            className={classes.sectionCard}
                            shadow="sm"
                            padding="lg"
                            radius="md"
                            withBorder
                            mb="md"
                            sx={{ borderLeft: `4px solid ${borderColor}` }}
                        >
                            <Group mb="md" spacing="sm">
                                <Text className={classes.sectionTitle} sx={{ color: borderColor }}>
                                    {section.title}
                                </Text>
                                <Badge
                                    color={section.category === 'usage' ? 'cyan' : section.category === 'content' ? 'green' : 'violet'}
                                    variant="light"
                                    size="xs"
                                    radius="sm"
                                    sx={{ fontFamily: "'Outfit', sans-serif", flexShrink: 0 }}
                                >
                                    {CAT_LABEL[section.category]}
                                </Badge>
                            </Group>

                            {section.collapsible ? (
                                <Accordion variant="filled" radius="md">
                                    <Accordion.Item value="content">
                                        <Accordion.Control
                                            sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: rem(14), color: borderColor }}
                                        >
                                            Read full section
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            {renderProse(section.content)}
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                </Accordion>
                            ) : (
                                renderProse(section.content)
                            )}
                        </Card>
                    )
                })}

                {/* ── Contact callout ── */}
                <Divider
                    className={classes.sectionDivider}
                    label={<span className={classes.dividerLabel}>Contact us</span>}
                    labelPosition="center"
                    size="xs"
                />

                <div className={classes.contactCard}>
                    <Group position="center" spacing="xs" mb="xs">
                        <IconMail size={22} color="#0d6a7a" />
                        <Text fw={700} sx={{ fontFamily: "'Outfit', sans-serif", color: '#0d6a7a' }}>
                            Questions about these terms?
                        </Text>
                    </Group>
                    <Anchor href="mailto:info@getcomedyseen.com" underline={false}>
                        <Text fw={700} sx={{ fontFamily: "'Outfit', sans-serif", fontSize: rem(18), color: '#0d6a7a' }}>
                            info@getcomedyseen.com
                        </Text>
                    </Anchor>
                    <Text size="xs" color="dimmed" mt={6}>We'll respond within 1–2 business days</Text>
                </div>

                <Space h="xl" />
            </Container>

            <Footer links={links} />
        </div>
    )
}

export default Tos;